<template>
  <div id="fourSeasonsTab">
    <loading-spinner v-if="!fourSeasons" />
    <div v-else>
      <b-form-group :label="getLabel('FOUR_SEASONS_FILM_TYPE')">
        <b-form-select
          v-if="fsfilmtypes"
          v-model="selectedFSFilmType"
          :options="allFilmTypes"
          value-field="id"
          text-field="name"
        />
      </b-form-group>

      <h4 class="mt-5" v-label-key="'FOUR_SEASONS_PACKS_ACTIVATED'">
        Packs Activated
      </h4>
      <b-button variant="outline-success" @click="downloadPacksActivatedList()">
        <span v-label-key="'FOUR_SEASONS_DOWNLOAD_HISTORY'"
          >Download History</span
        >
      </b-button>
      <b-table-simple v-if="fourSeasons" striped class="mt-3">
        <b-thead>
          <b-tr>
            <b-th><span v-label-key="'DATE'">Date</span></b-th>
            <b-th><span v-label-key="'COUNT'">Count</span></b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="record in aggregate(
              fourSeasons.packs_activated,
              'registered'
            )"
            :key="record.date"
          >
            <b-td>{{ record.date }}</b-td>
            <b-td>{{ record.count }}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <h4 class="mt-5" v-label-key="'FOUR_SEASONS_PACKS_ISSUED'">
      Packs Issued
    </h4>
    <b-button variant="outline-success" @click="downloadPacksIssuedList()">
      <span v-label-key="'FOUR_SEASONS_DOWNLOAD_HISTORY'"
        >Download History</span
      >
    </b-button>
    <b-table-simple v-if="fourSeasons" striped class="mt-3">
      <b-thead>
        <b-tr>
          <b-th><span v-label-key="'DATE'">Date</span></b-th>
          <b-th><span v-label-key="'COUNT'">Count</span></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="record in aggregate(fourSeasons.packs_issued, 'created')"
          :key="record.date"
        >
          <b-td>{{ record.date }}</b-td>
          <b-td>{{ record.count }}</b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>

    <h4 class="mt-5" v-label-key="'FOUR_SEASONS_VOUCHERS_USED'">
      Vouchers Used
    </h4>
    <b-button variant="outline-success" @click="downloadVouchersList()">
      <span v-label-key="'FOUR_SEASONS_DOWNLOAD_HISTORY'"
        >Download History</span
      >
    </b-button>
    <b-table-simple v-if="fourSeasons" striped class="mt-3">
      <b-thead>
        <b-tr>
          <b-th><span v-label-key="'DATE'">Date</span></b-th>
          <b-th><span v-label-key="'COUNT'">Count</span></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="record in aggregate(fourSeasons.vouchers_used, 'used_time')"
          :key="record.id"
        >
          <b-td>{{ record.date }}</b-td>
          <b-td>{{ record.count }}</b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </div>
</template>

<script>
import $auth from '@/services/auth';
import $api from '@/services/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import $csv from '@/services/csv';
import $excel from '@/services/excel';
import { mapGetters } from 'vuex';

export default {
  name: 'FourSeasonsTab',

  components: {
    LoadingSpinner,
  },

  data() {
    return {
      auth: $auth,
      fourSeasons: null,
      fsfilmtypes: null,
      selectedFSFilmType: '',
    };
  },

  computed: {
    allFilmTypes() {
      return [
        { id: '', name: this.getLabel('FOUR_SEASONS_ALL_FILMTYPES') },
      ].concat(this.fsfilmtypes);
    },
    ...mapGetters('translations', {
      getLabel: 'getLabel',
    }),
  },

  watch: {
    selectedFSFilmType() {
      this.refreshData();
    },
  },

  async created() {
    await $api.get('/v1/dashboard/fsfilmtypes').then(resp => {
      this.fsfilmtypes = resp;
    });
    this.refreshData();
  },

  methods: {
    async refreshData() {
      await $api
        .get(
          `/v1/dashboard/chains/${this.$route.params.id}/fsreports?fsfilmtype_id=${this.selectedFSFilmType}`
        )
        .then(resps => {
          this.fourSeasons = resps;
        });
    },

    addFSPacks() {
      const count = prompt(this.getLabel('FOUR_SEASONS_ISSUE_HOW_MANY')); // eslint-disable-line no-alert
      if (count > 0) {
        this.chains.fspacks_purchased += count * 1;
        $api.post(
          `/v1/dashboard/chains/${this.$route.params.id}/fspackpurchases`,
          {
            count,
          }
        );
      }
    },

    downloadVouchersList() {
      const items = this.filterByFSFilmType(this.fourSeasons.vouchers_used);
      const headerKeys = [
        'VOUCHER_ID',
        'FOUR_SEASON_PACK_ID',
        'FOUR_SEASON_USED_TIME',
        'FOUR_SEASON_USED_STORE_ID',
        'FOUR_SEASON_USED_STORE_NAME',
        'FOUR_SEASONS_OWNER_NUMBER',
        'ISSUING_STORE_ID',
        'FOUR_SEASONS_BUYER_NUMBER',
      ];
      const headers = headerKeys.map(this.getLabel);
      const fields = [
        'voucher_id',
        'fspack_id',
        'used_time',
        'used_store',
        'used_store_name',
        'owner_number',
        'issuing_store_id',
        'buyer_number',
      ];
      // $csv.export(items, fields, 'fsvouchers.csv');
      $excel.export(items, fields, 'fsvouchers', headers);
    },

    downloadPacksIssuedList() {
      const packs = this.filterByFSFilmType(this.fourSeasons.packs_issued);
      const headerKeys = [
        'PACK_ID',
        'CHAIN_ID',
        'STORE_ID',
        'STORE_NAME',
        'WARRANTY_FIELD_CUSTOMER_NUMBER',
        'CREATED',
        'VOUCHER_IDS',
      ];
      const headers = headerKeys.map(this.getLabel);
      const fields = [
        'pack_id',
        'chain_id',
        'store_id',
        'store_name',
        'customer_number',
        'created',
        'voucher_ids',
      ];
      // $csv.export(packs, fields, 'fspacksissued.csv');
      $excel.export(packs, fields, 'fspacksissued', headers);
    },

    downloadPacksActivatedList() {
      const packs = this.filterByFSFilmType(this.fourSeasons.packs_activated);
      const headerKeys = [
        'ID',
        'CHAIN_ID',
        'STORE_ID',
        'STORE_NAME',
        'CREATED',
        'REGISTERED',
      ];
      const headers = headerKeys.map(this.getLabel);
      const fields = [
        'id',
        'chain_id',
        'store_id',
        'store_name',
        'created',
        'registered',
      ];
      // $csv.export(packs, fields, 'fspacksactivated.csv');
      $excel.export(packs, fields, 'fspacksactivated', headers);
    },

    filterByFSFilmType(data) {
      return data.filter(
        item =>
          !this.selectedFSFilmType ||
          item.fsfilmtype_id === this.selectedFSFilmType
      );
    },

    aggregate(data, field) {
      const counts = {};
      data.forEach(item => {
        const targetDate = item[field].split('-');
        const month = `${targetDate[0]}-${targetDate[1]}`;
        counts[month] = counts[month] || 0;
        if (
          !this.selectedFSFilmType ||
          item.fsfilmtype_id === this.selectedFSFilmType
        ) {
          counts[month] += 1;
        }
      });
      return this.ensureZeroDates(
        Object.keys(counts).map(month => ({
          date: month,
          count: counts[month],
        }))
      );
    },

    ensureZeroDates(data) {
      // Ensure that months with 0 values still appear
      const requiredDates = [];
      const currentMonth = new Date().getMonth();
      for (let i = 1; i <= 12; i += 1) {
        if (i <= currentMonth + 1) {
          requiredDates.push(`2020-${`${i}`.length === 1 ? '0' : ''}${i}`);
        }
      }
      requiredDates.forEach(requiredDate => {
        if (!data.some(d => d.date === requiredDate)) {
          data.push({
            date: requiredDate,
            count: 0,
          });
        }
      });

      // data = data.slice(0, data.length).sort((a, b) => (new Date(b.date) - new Date(a.date)));
      return data
        .slice(0, data.length)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },
};
</script>
